/**
 * Lich su cuoc va rut cuoc som.
 *
 * Ba diem nghiep vu quyet dinh man hinh nay:
 *
 *   1. Man hinh doc ANH CHUP luc dat cuoc (SPT-R24), khong doc du lieu hien tai.
 *      Ty le, ten su kien, tham so thi truong deu la thu da chot; hien so hien
 *      tai thi phieu cu doc ra sai.
 *   2. Gia chao rut som SONG RAT NGAN (SPT-R41) — muoi giay. Man hinh hien dong
 *      ho dem nguoc va tu lay gia moi; khong bao gio cho bam xac nhan bang mot
 *      con so da chet.
 *   3. Rut som KHONG DAO NGUOC (SPT-R42). Phieu chuyen sang da rut som va khong
 *      quyet toan lai theo ket qua tran, ke ca khi tran sau do dien ra co loi.
 *      Cau nay phai nam ngay canh nut, khong phai trong dieu khoan.
 */

import { useEffect, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  ApiError,
  type BetLeg,
  type BetRow,
  type CashoutQuote,
} from '../lib/api';
import { formatMoney } from '../lib/money';
import { useSession } from '../lib/session';
import { Badge, Button, EmptyState, ErrorList, Skeleton } from '../components/ui';
import styles from './Bets.module.css';

type Tone = 'neutral' | 'positive' | 'negative' | 'warning';

const BET_STATUS: Readonly<Record<string, { label: string; tone: Tone }>> = {
  OPEN: { label: 'Đang chờ', tone: 'warning' },
  WON: { label: 'Thắng', tone: 'positive' },
  LOST: { label: 'Thua', tone: 'neutral' },
  VOID: { label: 'Hủy', tone: 'neutral' },
  HALF_WON: { label: 'Thắng nửa', tone: 'positive' },
  HALF_LOST: { label: 'Thua nửa', tone: 'neutral' },
  CASHED_OUT: { label: 'Đã rút sớm', tone: 'positive' },
  REJECTED: { label: 'Bị từ chối', tone: 'negative' },
};

const LEG_RESULT: Readonly<Record<string, { label: string; tone: Tone }>> = {
  WIN: { label: 'Thắng', tone: 'positive' },
  LOSE: { label: 'Thua', tone: 'negative' },
  VOID: { label: 'Hủy', tone: 'neutral' },
  HALF_WIN: { label: 'Thắng nửa', tone: 'positive' },
  HALF_LOSE: { label: 'Thua nửa', tone: 'negative' },
};

/** Ma ly do khong chao rut som -> cau chu hien cho nguoi choi. */
const CASHOUT_REASON: Readonly<Record<string, string>> = {
  BET_NOT_OPEN: 'Phiếu không còn mở nên không rút sớm được.',
  SYSTEM_BET_UNSUPPORTED: 'Cược hệ thống chưa hỗ trợ rút sớm.',
  LEG_ALREADY_LOST: 'Phiếu đã có chặng thua.',
  MARKET_SUSPENDED: 'Có chặng đang tạm dừng nên chưa chào giá được.',
  LIVE_FEED_STALE: 'Dữ liệu trận đấu đang gián đoạn, tạm ngừng chào rút sớm.',
  READY_TO_SETTLE: 'Phiếu đã đủ điều kiện quyết toán, chờ chi trả.',
  AMOUNT_TOO_SMALL: 'Giá chào quá nhỏ để rút sớm.',
};

const MARKET_TYPE: Readonly<Record<string, string>> = {
  '1X2': 'Kèo 1X2',
  ASIAN_HANDICAP: 'Kèo chấp',
  DOUBLE_CHANCE: 'Kèo đúp',
  OVER_UNDER: 'Tài / Xỉu',
  BOTH_TEAMS_TO_SCORE: 'Hai đội cùng ghi bàn',
  CORRECT_SCORE: 'Tỷ số chính xác',
  MONEYLINE: 'Kèo thắng thua',
  TOTALS: 'Tổng điểm',
};

const BET_TYPE: Readonly<Record<string, string>> = {
  SINGLE: 'Cược đơn',
  MULTI: 'Cược xâu',
  SYSTEM: 'Cược hệ thống',
  CHAIN: 'Cược chuỗi',
};

const FILTERS = [
  { key: null, label: 'Tất cả' },
  { key: 'OPEN', label: 'Đang chờ' },
  { key: 'WON', label: 'Thắng' },
  { key: 'LOST', label: 'Thua' },
] as const;

export function BetsPage(): JSX.Element {
  const { isAuthenticated } = useSession();
  const [status, setStatus] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const bets = useQuery({
    queryKey: ['bets', status],
    queryFn: () => api.bets.list(status ? { status } : {}),
    enabled: isAuthenticated,
  });

  if (!isAuthenticated) return <EmptyState title="Đăng nhập để xem lịch sử cược" />;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Cược của tôi</h1>

      <div className={styles.filters}>
        {FILTERS.map((f) => (
          <button
            key={f.label}
            className={[styles.filter, f.key === status ? styles.filterActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => setStatus(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {bets.isLoading ? (
        <Skeleton height={200} />
      ) : bets.data && bets.data.bets.length > 0 ? (
        <ul className={styles.list}>
          {bets.data.bets.map((bet) => (
            <BetCard
              key={bet.bet_id}
              bet={bet}
              expanded={expanded === bet.bet_id}
              onToggle={() => setExpanded(expanded === bet.bet_id ? null : bet.bet_id)}
            />
          ))}
        </ul>
      ) : (
        <EmptyState
          title="Chưa có phiếu cược nào"
          description="Kèo thể thao đang mở ở mục Thể thao."
          action={<Link to="/the-thao"><Button>Xem kèo</Button></Link>}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function BetCard({
  bet,
  expanded,
  onToggle,
}: {
  bet: BetRow;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const status = BET_STATUS[bet.status] ?? { label: bet.status, tone: 'neutral' as const };
  const currency = bet.stake.currency;

  const detail = useQuery({
    queryKey: ['bet', bet.bet_id],
    queryFn: () => api.bets.get(bet.bet_id),
    enabled: expanded,
  });

  return (
    <li className={styles.bet}>
      <button className={styles.betHead} onClick={onToggle} aria-expanded={expanded}>
        <span className={styles.betType}>{BET_TYPE[bet.bet_type] ?? bet.bet_type}</span>
        <span className={styles.betDate}>
          {new Date(bet.placed_at).toLocaleString('vi-VN', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        <Badge tone={status.tone}>{status.label}</Badge>
        <span className={styles.betChevron} aria-hidden="true">{expanded ? '▾' : '▸'}</span>
      </button>

      <div className={styles.betFigures}>
        <Figure label="Tiền cược" value={`${formatMoney(bet.stake)} ${currency}`} />
        <Figure label="Tỷ lệ" value={bet.total_odds ?? '—'} />
        {/* Phieu da xong thi hien tien THUC NHAN; con mo thi hien tien co the thang.
            Gop hai thu vao mot cot la cach nhanh nhat de nguoi choi doc nham. */}
        {bet.payout !== null ? (
          <Figure
            label="Thực nhận"
            value={`${formatMoney(bet.payout)} ${currency}`}
            tone={BigInt(bet.payout.amount_minor) > 0n ? 'positive' : undefined}
          />
        ) : (
          <Figure label="Có thể thắng" value={`${formatMoney(bet.potential_win)} ${currency}`} />
        )}
      </div>

      {bet.status === 'OPEN' && <CashoutPanel betId={bet.bet_id} />}

      {expanded && (
        <div className={styles.legs}>
          {detail.isLoading ? (
            <Skeleton height={60} />
          ) : detail.data?.bet ? (
            detail.data.bet.legs.map((leg) => <LegRow key={leg.selection_id} leg={leg} />)
          ) : (
            <p className={styles.muted}>Không đọc được chi tiết phiếu.</p>
          )}
        </div>
      )}
    </li>
  );
}

function LegRow({ leg }: { leg: BetLeg }): JSX.Element {
  const result = leg.result ? LEG_RESULT[leg.result] : null;
  const snapshot = leg.snapshot ?? {};

  return (
    <div className={styles.leg}>
      <div className={styles.legMain}>
        {/* Doc tu anh chup, khong doc lai du lieu hien tai (SPT-R24) */}
        <span className={styles.legEvent}>{snapshot.event_name ?? snapshot.event_id ?? '—'}</span>
        <span className={styles.legMarket}>
          {snapshot.market_type
            ? (MARKET_TYPE[snapshot.market_type] ?? snapshot.market_type)
            : '—'}
          {snapshot.market_parameter ? ` ${snapshot.market_parameter}` : ''} ·{' '}
          {snapshot.selection_name ?? leg.selection_id}
        </span>
      </div>
      <span className={`numeric ${styles.legOdds}`}>{leg.odds}</span>
      {result ? (
        <Badge tone={result.tone}>{result.label}</Badge>
      ) : (
        <Badge tone="warning">Chưa có</Badge>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Bang rut cuoc som.
 *
 * Gia chao chi song muoi giay. Man hinh tu lay gia moi truoc khi gia cu chet,
 * va khoa nut trong khoanh khac giao thoi — bam xac nhan bang mot gia da het
 * han thi may chu tu choi, va tu choi o buoc cuoi lam nguoi choi mat long tin
 * hon la khong chao gia.
 */
function CashoutPanel({ betId }: { betId: string }): JSX.Element | null {
  const queryClient = useQueryClient();
  const [fractionBp, setFractionBp] = useState(10_000);
  const [now, setNow] = useState(() => Date.now());
  const [done, setDone] = useState<{ paidMinor: string; currency: string } | null>(null);

  const quote = useQuery<CashoutQuote>({
    queryKey: ['cashout-quote', betId, fractionBp],
    queryFn: () => api.bets.cashoutQuote(betId, fractionBp),
    // Lay gia moi truoc khi gia cu het han. Thoi han la muoi giay.
    refetchInterval: 7_000,
    retry: false,
    enabled: done === null,
  });

  const execute = useMutation({
    mutationFn: () => api.bets.cashout(betId, quote.data!.quoteId!),
    onSuccess: (result) => {
      setDone({ paidMinor: result.paidMinor, currency: quote.data?.currency ?? 'USD' });
      void queryClient.invalidateQueries({ queryKey: ['bets'] });
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
    },
  });

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, []);

  if (done) {
    return (
      <div className={styles.cashoutDone}>
        Đã rút sớm{' '}
        <strong className="numeric">
          {formatMoney({ minor: BigInt(done.paidMinor), currency: done.currency })} {done.currency}
        </strong>
        . Phiếu không quyết toán lại theo kết quả trận.
      </div>
    );
  }

  if (quote.isLoading) return <div className={styles.cashoutBar}><Skeleton height={32} /></div>;
  if (!quote.data) return null;

  if (!quote.data.available) {
    return (
      <div className={styles.cashoutBar}>
        {/* Doc theo MA, khong hien lai cau chu cua may chu: cau chu do viet cho
            nhat ky van hanh, khong phai cho nguoi choi doc. */}
        <span className={styles.muted}>
          {(quote.data.reasonCode && CASHOUT_REASON[quote.data.reasonCode]) ??
            'Chưa chào rút sớm cho phiếu này.'}
        </span>
      </div>
    );
  }

  const expiresIn = quote.data.expiresAt
    ? new Date(quote.data.expiresAt).getTime() - now
    : 0;
  const stale = expiresIn <= 500;
  const error = execute.error instanceof ApiError ? execute.error : null;

  return (
    <div className={styles.cashout}>
      <div className={styles.cashoutTop}>
        <div>
          <span className={styles.cashoutLabel}>Rút sớm ngay</span>
          <span className={`numeric ${styles.cashoutAmount}`}>
            {formatMoney({
              minor: BigInt(quote.data.amountMinor ?? '0'),
              currency: quote.data.currency ?? 'USD',
            })}{' '}
            {quote.data.currency}
          </span>
        </div>
        <span className={[styles.cashoutTimer, stale ? styles.cashoutTimerStale : ''].filter(Boolean).join(' ')}>
          {stale ? 'Đang lấy giá mới' : `Giá giữ ${Math.ceil(expiresIn / 1000)} giây`}
        </span>
      </div>

      {/* SPT-R43: rut mot phan chia phieu thanh phan da rut va phan con giu */}
      <div className={styles.fractions}>
        {[10_000, 5_000, 2_500].map((f) => (
          <button
            key={f}
            className={[styles.fraction, f === fractionBp ? styles.fractionActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => setFractionBp(f)}
          >
            {f === 10_000 ? 'Toàn bộ' : `${f / 100}%`}
          </button>
        ))}
      </div>

      {error && <ErrorList messages={error.messages} />}

      <Button
        size="sm"
        fullWidth
        loading={execute.isPending}
        disabled={stale}
        onClick={() => execute.mutate()}
      >
        Xác nhận rút sớm
      </Button>

      <p className={styles.cashoutWarn}>
        Rút sớm <strong>không hoàn tác được</strong>. Phần đã rút không quyết toán lại theo kết quả
        trận, kể cả khi kết quả sau đó có lợi. Doanh số cược vẫn tính theo tiền cược gốc.
      </p>
    </div>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'positive';
}): JSX.Element {
  return (
    <div className={styles.figure}>
      <span className={styles.figureLabel}>{label}</span>
      <span
        className={['numeric', styles.figureValue, tone === 'positive' ? styles.figurePositive : '']
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </span>
    </div>
  );
}
